import React, { useEffect, useRef } from "react";

interface TurnstileProps {
  onVerify: (token: string) => void;
  className?: string;
}

declare global {
  interface Window {
    turnstile: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        }
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const Turnstile: React.FC<TurnstileProps> = ({ onVerify, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);

  // Always keep onVerify callback up-to-date without triggering re-render
  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    let active = true;
    let intervalId: NodeJS.Timeout | null = null;

    const renderWidget = () => {
      if (active && window.turnstile && containerRef.current && !widgetIdRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey:
              import.meta.env.VITE_TURNSTILE_SITE_KEY ||
              (import.meta.env.DEV ? "1x00000000000000000000AA" : "0x4AAAAAABdn5ZgjXDJYE2EM"),
            callback: (token: string) => {
              onVerifyRef.current(token);
            },
            theme: "light",
          });
        } catch (err) {
          console.error("Error rendering Turnstile widget:", err);
        }
      }
    };

    // If turnstile is already loaded, render immediately
    if (window.turnstile) {
      renderWidget();
    } else {
      // Otherwise wait for it to be ready
      intervalId = setInterval(() => {
        if (window.turnstile) {
          renderWidget();
          if (intervalId) clearInterval(intervalId);
        }
      }, 500);
    }

    return () => {
      active = false;
      if (intervalId) clearInterval(intervalId);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.warn("Error removing Turnstile widget:", err);
        }
        widgetIdRef.current = null;
      }
    };
  }, []); // Run exactly once on mount to prevent infinite re-rendering loops

  return <div ref={containerRef} className={className} />;
};

export default Turnstile;
